/*
 *   File : manage-ams-form.js
 *   Author URI : https://evoqueinnovativelab.com
 *   Description : Create / Edit AMS modal
 *   Integrations : null
 *   Version : v1.0
 *   Created : 06-05-2026
 */

/* import packages */
import React, { useEffect, useMemo, useState } from "react";
import $ from "jquery";

/* import styles */
import styles from "../../styles/components/manageAMS.module.scss";

/* import components */
import { Toast } from "../note";

/* import assets */
// import InfoIcon from "../../assets/images/modal/info-filled.svg";

const MODAL_ID = "manageAMSForm";

export default function ManageAMSForm(props) {

    const isEdit = Boolean(props.data);
    const goals = useMemo(() => props.goals ?? [], [props.goals]);

    const [name, setName] = useState("");
    const [amsAlias, setAmsAlias] = useState("");
    const [allocations, setAllocations] = useState({});
    const [minSipInput, setMinSipInput] = useState("");
    const [minLumpsumInput, setMinLumpsumInput] = useState("");
    const [minInvestMoreInput, setMinInvestMoreInput] = useState("");
    /* Dirty = admin has typed in the field; stop auto-syncing from calc until reset.
     * Also set true when edit mode pre-fills from saved row so calc doesn't overwrite. */
    const [minSipDirty, setMinSipDirty] = useState(false);
    const [minLumpsumDirty, setMinLumpsumDirty] = useState(false);
    const [minInvestMoreDirty, setMinInvestMoreDirty] = useState(false);
    const [loader, setLoader] = useState(false);
    const [error, setError] = useState(null);

    /* default to even split across goals when creating, and pre-fill from props.data when editing */
    useEffect(() => {
        if (!goals.length) return;

        if (isEdit) {
            setName(props.data.name ?? "");
            setAmsAlias(props.data.ams_alias ?? "");
            const incoming = Array.isArray(props.data.allocations) ? props.data.allocations : [];
            const next = {};
            goals.forEach((g) => {
                const match = incoming.find((a) => Number(a.goal_id) === Number(g.id));
                next[g.id] = match ? String(match.allocation ?? 0) : "0";
            });
            setAllocations(next);
            /* If backend returns saved min_sip / min_lumpsum on the row, pre-fill
             * and lock against auto-sync so the saved override survives. */
            if (props.data.min_sip != null && props.data.min_sip !== "") {
                setMinSipInput(String(props.data.min_sip));
                setMinSipDirty(true);
            } else {
                setMinSipDirty(false);
            }
            if (props.data.min_lumpsum != null && props.data.min_lumpsum !== "") {
                setMinLumpsumInput(String(props.data.min_lumpsum));
                setMinLumpsumDirty(true);
            } else {
                setMinLumpsumDirty(false);
            }
            setMinInvestMoreDirty(false);
        } else {
            setName("");
            setAmsAlias("");
            const evenSplit = goals.length > 0 ? Math.floor(100 / goals.length) : 0;
            const remainder = 100 - evenSplit * goals.length;
            const next = {};
            goals.forEach((g, idx) => {
                next[g.id] = String(idx === 0 ? evenSplit + remainder : evenSplit);
            });
            setAllocations(next);
            setMinSipDirty(false);
            setMinLumpsumDirty(false);
            setMinInvestMoreDirty(false);
        }
    }, [props.data, isEdit, goals]);

    /* clear state and remove backdrop when modal closes */
    useEffect(() => {
        const modalEl = document.getElementById(MODAL_ID);
        if (!modalEl) return;
        const handler = () => {
            setLoader(false);
            setError(null);
            $(".modal-backdrop").remove();
            $("body").removeAttr("style");
            if (props.closeModal) props.closeModal();
        };
        modalEl.addEventListener("hidden.bs.modal", handler);
        return () => modalEl.removeEventListener("hidden.bs.modal", handler);
    }, [props]);


    const _handleAllocationChange = (key, raw) => {
        const cleaned = raw.replace(/[^0-9.]/g, "");
        if (cleaned === "" || (Number(cleaned) >= 0 && Number(cleaned) <= 100)) {
            setAllocations((prev) => ({ ...prev, [key]: cleaned }));
        }
    };

    const totalAllocation = useMemo(() => goals.reduce((acc, g) => {
        const v = parseFloat(allocations[g.id]);
        return acc + (Number.isFinite(v) ? v : 0);
    }, 0), [goals, allocations]);

    /* AMS minimum (per backend rule):
     *   required_for_basket = basket_min * 100 / allocation_pct
     *   ams_min = max(required_for_each_basket)
     * Skip baskets with 0% allocation (no contribution, avoids div-by-zero).
     * Math.ceil so the rounded amount still satisfies the basket min
     * (e.g. 5000/0.70 = 7142.857 → ₹7,143; ₹7,142 would short the basket). */
    const { minSipAmount, minLumpsumAmount, minInvestMoreAmount } = useMemo(() => {
        let sipMax = 0;
        let lumpsumMax = 0;
        let investMoreMax = 0;
        goals.forEach((g) => {
            const pct = parseFloat(allocations[g.id]);
            if (!Number.isFinite(pct) || pct <= 0) return;
            const sipMin = Number(g.min_sip_amount) || 0;
            const lumpMin = Number(g.min_lumpsum_amount) || 0;
            const investMoreMin = Number(g.min_invest_more_amount) || 0;
            const reqSip = Math.ceil((sipMin * 100) / pct);
            const reqLump = Math.ceil((lumpMin * 100) / pct);
            const reqInvestMore = Math.ceil((investMoreMin * 100) / pct);
            if (reqSip > sipMax) sipMax = reqSip;
            if (reqLump > lumpsumMax) lumpsumMax = reqLump;
            if (reqInvestMore > investMoreMax) investMoreMax = reqInvestMore;
        });
        return { minSipAmount: sipMax, minLumpsumAmount: lumpsumMax, minInvestMoreAmount: investMoreMax };
    }, [goals, allocations]);

    useEffect(() => {
        if (error !== null) {
            const t = setTimeout(() => setError(null), 10000);
            return () => clearTimeout(t);
        }
    }, [error]);

    /* Auto-fill of the calculated minimum into the input fields is disabled:
     * the inputs now show the admin-configured value from the API, and the
     * calculated minimum is surfaced as a contextual label next to each title. */
    // useEffect(() => {
    //     if (minSipDirty) return;
    //     setMinSipInput(minSipAmount > 0 ? String(minSipAmount) : "");
    // }, [minSipAmount, minSipDirty]);

    // useEffect(() => {
    //     if (minLumpsumDirty) return;
    //     setMinLumpsumInput(minLumpsumAmount > 0 ? String(minLumpsumAmount) : "");
    // }, [minLumpsumAmount, minLumpsumDirty]);

    useEffect(() => {
        if (minInvestMoreDirty) return;

        if (isEdit && props.data) {
            const saved = props.data.min_invest_more ?? props.data.min_invest_more_amount;
            if (saved != null && saved !== "") {
                setMinInvestMoreInput(String(saved));
                setMinInvestMoreDirty(true);
                return;
            }
        }

        setMinInvestMoreInput(minInvestMoreAmount > 0 ? String(minInvestMoreAmount) : "");
    }, [minInvestMoreAmount, minInvestMoreDirty, isEdit, props.data]);

    /* Name and all minimum amounts are required — the submit button stays
     * disabled until they're filled (same as the name). AMS Alias is optional. */
    const allFilled = name.trim().length > 0
        && minSipInput.trim() !== ""
        && minLumpsumInput.trim() !== ""
        && minInvestMoreInput.trim() !== ""
        && goals.length > 0
        && goals.every((g) => allocations[g.id] !== "" && allocations[g.id] !== null && allocations[g.id] !== undefined);

    const isValid = allFilled && Math.round(totalAllocation * 100) / 100 === 100;

    const _handleSubmit = () => {
        if (!isValid) {
            const sum = Math.round(totalAllocation * 100) / 100;
            setError(`Allocations must total 100%. Current total is ${sum}%.`);
            return;
        }

        const parsedMinSip = parseFloat(minSipInput);
        const parsedMinLumpsum = parseFloat(minLumpsumInput);
        const parsedMinInvestMore = parseFloat(minInvestMoreInput);

        /* Admin overrides may raise the AMS minimum (e.g. for a stricter floor)
         * but must never go below the basket-derived requirement, otherwise the
         * smallest-allocation basket won't hit its own minimum.
         * Collect both violations so a toast covers everything at once instead
         * of making the admin submit twice to discover the second error. */
        const violations = [];
        if (Number.isFinite(parsedMinSip) && parsedMinSip < minSipAmount) {
            violations.push(`SIP ₹${parsedMinSip.toLocaleString('en-IN')} is below the calculated minimum of ₹${minSipAmount.toLocaleString('en-IN')}`);
        }
        if (Number.isFinite(parsedMinLumpsum) && parsedMinLumpsum < minLumpsumAmount) {
            violations.push(`Lumpsum ₹${parsedMinLumpsum.toLocaleString('en-IN')} is below the calculated minimum of ₹${minLumpsumAmount.toLocaleString('en-IN')}`);
        }
        if (Number.isFinite(parsedMinInvestMore) && parsedMinInvestMore < minInvestMoreAmount) {
            violations.push(`Additional amount ₹${parsedMinInvestMore.toLocaleString('en-IN')} is below the calculated minimum of ₹${minInvestMoreAmount.toLocaleString('en-IN')}`);
        }
        if (violations.length > 0) {
            setError(`Minimum AMS amount invalid — ${violations.join("; ")}.`);
            return;
        }

        setLoader(true);

        const payload = {
            id: isEdit ? props.data.id : null,
            name: name.trim(),
            ams_alias: amsAlias.trim() || null,
            min_sip: Number.isFinite(parsedMinSip) ? parsedMinSip : minSipAmount,
            min_lumpsum: Number.isFinite(parsedMinLumpsum) ? parsedMinLumpsum : minLumpsumAmount,
            min_invest_more: Number.isFinite(parsedMinInvestMore) ? parsedMinInvestMore : minInvestMoreAmount,
            allocations: goals.map((g) => ({
                goal_id: g.id,
                allocation: parseFloat(allocations[g.id]) || 0,
            })),
        };

        Promise.resolve(props.onSubmit && props.onSubmit(payload, isEdit))
            .catch((errMsg) => {
                if (typeof errMsg === "string") setError(errMsg);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    return (
        <div className={`${styles.e_modal} modal fade`}
            id={MODAL_ID}
            tabIndex="-1"
            data-bs-backdrop="static"
            aria-labelledby="manageAMSForm"
            aria-hidden="true">
            <div className={`${styles.e_modal_dialog} modal-dialog modal-dialog-centered`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h6>{isEdit ? "Edit Core AMS" : "Create Core AMS"}</h6>
                        <button type="button"
                            className="btn-close"
                            id="ams-form-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            disabled={loader} />
                        <button type="button"
                            className="d-none"
                            id="ams-form-dismiss"
                            data-bs-dismiss="modal"
                            aria-label="Close" />
                    </div>
                    <div className="container px-3">
                        <div className="row">
                            <div className="col-12 mb-4">
                                <label className={styles.e_field_label}>AMS name</label>
                                <input type="text"
                                    className={styles.e_modal_input}
                                    placeholder="Enter name"
                                    value={name}
                                    disabled={loader}
                                    maxLength={60}
                                    onChange={(e) => setName(e.target.value)} />
                            </div>

                            <div className="col-12 mb-4">
                                <label className={styles.e_field_label}>AMS Alias</label>
                                {/* <span className={styles.e_field_hint}>(User Friendly Name for Internal Use)</span> */}
                                <input type="text"
                                    className={styles.e_modal_input}
                                    placeholder="Enter alias"
                                    value={amsAlias}
                                    disabled={loader}
                                    maxLength={120}
                                    onChange={(e) => setAmsAlias(e.target.value)} />
                            </div>

                            <div className="col-12 mb-2">
                                <span className={styles.e_section_label}>Basket Allocations</span>
                            </div>

                            {goals.length === 0 ? (
                                <div className="col-12 text-center py-3">
                                    <p className={styles.e_info_text}>Loading baskets…</p>
                                </div>
                            ) : (
                                goals.map((goal, idx) => (
                                    <div key={goal.id} className="col-12 mb-2">
                                        {idx === 0 && (
                                            <div className="row mb-1">
                                                <div className="col-lg-6 col-md-6 col-5">
                                                    <span className={styles.e_field_label}>Basket</span>
                                                </div>
                                                <div className="col-lg-2 col-md-3 col-3">
                                                    <span className={styles.e_field_label}>Allocation %</span>
                                                </div>
                                                <div className="col-lg-4 col-md-3 col-4">
                                                    <span className={styles.e_field_label}>Basket minimum</span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="row align-items-center">
                                            <div className="col-lg-6 col-md-6 col-5">
                                                <label
                                                    htmlFor={`ams-alloc-${goal.id}`}
                                                    className={styles.e_basket_name}
                                                >
                                                    {String(goal.name ?? "").replace(/\n/g, " ")}
                                                </label>
                                            </div>
                                            <div className="col-lg-2 col-md-3 col-3">
                                                <input
                                                    id={`ams-alloc-${goal.id}`}
                                                    type="text"
                                                    inputMode="decimal"
                                                    className={`${styles.e_modal_input} ${styles.e_bordered_input} ${styles.e_allocation_input}`}
                                                    value={allocations[goal.id] ?? ""}
                                                    disabled={loader}
                                                    onChange={(e) => _handleAllocationChange(goal.id, e.target.value)} />
                                            </div>
                                            <div className="col-lg-4 col-md-3 col-4">
                                                <span className={styles.e_basket_min_hint}>
                                                    SIP: ₹{Number(goal.min_sip_amount ?? 0).toLocaleString('en-IN')}
                                                </span>
                                                <span className={styles.e_basket_min_hint}>
                                                    Lumpsum: ₹{Number(goal.min_lumpsum_amount ?? 0).toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}

                            <div className="col-12 mb-2 mt-2">
                                <span className={styles.e_section_label}>Minimum AMS amount required</span>
                            </div>
                            <div className="col-12 mb-3">
                                <div className={`row align-items-center ${styles.e_min_amount_row}`}>
                                    <div className="col-4">
                                        <label className={styles.e_field_label}>Minimum SIP amount (₹)</label>
                                        {minSipAmount > 0 && (
                                            <span className={styles.e_calc_min_hint}>
                                                Calculated minimum: ₹{minSipAmount.toLocaleString('en-IN')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-8">
                                        <input type="text"
                                            inputMode="decimal"
                                            className={`${styles.e_modal_input} ${styles.e_bordered_input}`}
                                            value={minSipInput}
                                            disabled={loader}
                                            onChange={(e) => {
                                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                                setMinSipDirty(true);
                                                setMinSipInput(cleaned);
                                            }} />
                                    </div>
                                </div>
                                <div className={`row align-items-center ${styles.e_min_amount_row}`}>
                                    <div className="col-4">
                                        <label className={styles.e_field_label}>Minimum Lumpsum amount (₹)</label>
                                        {minLumpsumAmount > 0 && (
                                            <span className={styles.e_calc_min_hint}>
                                                Calculated minimum: ₹{minLumpsumAmount.toLocaleString('en-IN')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-8">
                                        <input type="text"
                                            inputMode="decimal"
                                            className={`${styles.e_modal_input} ${styles.e_bordered_input}`}
                                            value={minLumpsumInput}
                                            disabled={loader}
                                            onChange={(e) => {
                                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                                setMinLumpsumDirty(true);
                                                setMinLumpsumInput(cleaned);
                                            }} />
                                    </div>
                                </div>
                                <div className={`row align-items-center ${styles.e_min_amount_row}`}>
                                    <div className="col-4">
                                        <label className={styles.e_field_label}>Minimum additional amount (₹)</label>
                                        {minInvestMoreAmount > 0 && (
                                            <span className={styles.e_calc_min_hint}>
                                                Calculated minimum: ₹{minInvestMoreAmount.toLocaleString('en-IN')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-8">
                                        <input type="text"
                                            inputMode="decimal"
                                            className={`${styles.e_modal_input} ${styles.e_bordered_input}`}
                                            value={minInvestMoreInput}
                                            disabled={loader}
                                            onChange={(e) => {
                                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                                setMinInvestMoreDirty(true);
                                                setMinInvestMoreInput(cleaned);
                                            }} />
                                    </div>
                                </div>
                            </div>

                            {/* <div className="col-12">
                                <div className={styles.e_info_box}>
                                    <img src={InfoIcon} alt="" className={styles.e_info_icon} />
                                    <p className={styles.e_info_text}>
                                        Basket names are predefined and cannot be edited. You can adjust the allocation for each basket.
                                    </p>
                                </div>
                            </div> */}
                        </div>

                        <div className="row pt-3 pb-2">
                            <div className="col-12 d-flex justify-content-end gap-3">
                                <button type="button"
                                    className={styles.e_modal_cancel}
                                    data-bs-dismiss="modal"
                                    disabled={loader}>
                                    Cancel
                                </button>
                                <button type="button"
                                    className={`${styles.e_modal_submit} ${loader ? "e-btn-loader" : ""}`}
                                    disabled={!allFilled || loader}
                                    onClick={_handleSubmit}>
                                    {isEdit ? "Update AMS" : "Create AMS"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {error !== null ? <Toast data={error} type="error" id="ams-form-error" onClose={() => setError(null)} /> : null}
        </div>
    );
}
